/**
 * Device-local memory for the teams feature (eager, tiny, type-only imports): the rail badge,
 * the player's team, join requests sent, and the day the daily help cap was hit. Everything is
 * tagged with the player id, so a switched account never sees another account's memo, and every
 * read/write survives a broken store. The war feature keeps its own memo (war/memo.ts) through
 * the same player-tagged helpers.
 */
import type { AppContext } from '../../game/context.js';

type Ctx = Pick<AppContext, 'services'>;

const TEAM_KEY = 'feature.teams.memo';

/** A join request remembered on this device is a hint only: it is forgotten after this long. */
export const REQUEST_HINT_MS = 3 * 86_400_000;

export interface JoinHint {
  id: string;
  /** device clock when the request was sent */
  at: number;
}

export interface TeamMemo {
  /** the player's team at the last fetch (null: not in a team) */
  teamId: string | null;
  /** rail badge: join requests to answer (leaders) + help requests the player can fill */
  badge: number;
  /**
   * Invite-only teams the player asked to join from this device (no API lists them). A hint:
   * the leader may have declined, so the join button stays usable ("Ask again"); entries expire
   * after REQUEST_HINT_MS and the list clears once the player is in any team (the API deletes
   * every other request then).
   */
  requested: JoinHint[];
  /** the daily help cap (help_limit) holds until this server instant: help requests leave the badge */
  helpCappedUntil?: number;
}

const me = (a: Ctx): string | null => a.services.store.state.auth?.playerId ?? null;

/** Read a player-tagged memo (null when missing, another player's, or unreadable). */
export function readMemo<T>(a: Ctx, key: string): T | null {
  try {
    const raw = a.services.kv.get(key);
    if (!raw) return null;
    const box = JSON.parse(raw) as { p?: string | null; v?: T };
    return box.p === me(a) && box.v !== undefined ? box.v : null;
  } catch {
    return null;
  }
}

/** Write (or with null, drop) a player-tagged memo. */
export function writeMemo<T>(a: Ctx, key: string, v: T | null): void {
  try {
    if (v === null) a.services.kv.remove(key);
    else a.services.kv.set(key, JSON.stringify({ p: me(a), v }));
  } catch {
    // storage full or blocked: the memo is a convenience only
  }
}

/** Live join hints (older memos stored bare ids: those count as expired). */
function liveHints(raw: unknown, now: number): JoinHint[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((h): h is JoinHint => !!h && typeof h === 'object' && typeof (h as JoinHint).id === 'string' && typeof (h as JoinHint).at === 'number' && now - (h as JoinHint).at < REQUEST_HINT_MS);
}

export function teamMemo(a: Ctx): TeamMemo {
  const m = readMemo<Partial<TeamMemo>>(a, TEAM_KEY);
  return {
    teamId: m?.teamId ?? null,
    badge: m?.badge ?? 0,
    requested: liveHints(m?.requested, a.services.now()),
    ...(m?.helpCappedUntil ? { helpCappedUntil: m.helpCappedUntil } : {}),
  };
}

export function setTeamMemo(a: Ctx, patch: Partial<TeamMemo>): void {
  writeMemo(a, TEAM_KEY, { ...teamMemo(a), ...patch });
}

/** Ids of the invite-only teams the player asked to join lately (on this device). */
export const requestedIds = (a: Ctx): string[] => teamMemo(a).requested.map((h) => h.id);

/** Remember (or refresh) a join request sent now. */
export function rememberRequest(a: Ctx, teamId: string): void {
  const rest = teamMemo(a).requested.filter((h) => h.id !== teamId);
  setTeamMemo(a, { requested: [...rest, { id: teamId, at: a.services.now() }].slice(-20) });
}
